import CloseRounded from '@mui/icons-material/CloseRounded';
import React__default, { useRef, useLayoutEffect } from 'react';
import { OverlayTriggerStateContext, ModalOverlay, Modal, Dialog, DialogTrigger } from 'react-aria-components';
import { Button } from '../atoms/Button.js';
import { Text } from '../atoms/Text.js';
import { toSpacingAttributes } from '../layouts/Spacing/spacing.js';
import { isButtonComponent } from '../utils/isButtonComponent.js';
import './Drawer.scss';

/**
 * A drawer component that slides in from the left or right side of the screen.
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 * 		<Drawer.Content>
 *     		<div>Drawer content</div>
 * 		</Drawer.Content>
 *   </Drawer>
 * </DrawerTrigger>
 * ```
 *
 * Or with a render function to access the close function:
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 *     <Drawer.Header>Title</Drawer.Header>
 *     <Drawer.Content>
 *      {(close) => (
 *          <div>Drawer content</div>
 *          <Button onPress={close}>Close</Button>
 *      )}
 *     </Drawer.Content>
 *  </Drawer>
 * </DrawerTrigger>
 *
 *
 * ```
 */
const Drawer = ({ children, hasCloseButton = true, description, isDismissable = true, isKeyboardDismissDisabled = false, initialFocus = 'input', placement = 'right', size = 'medium', hideOverlay, className, ariaLabel, rounding = '0', }) => {
    const drawerRef = useRef(null);
    const drawerState = React__default.useContext(OverlayTriggerStateContext);
    const slots = {};
    // Generate unique IDs for aria-labelledby and aria-describedby
    const titleId = React__default.useId();
    const descriptionId = React__default.useId();
    // Focus the initial element when the drawer opens
    useLayoutEffect(() => {
        if (initialFocus && drawerRef.current && drawerState.isOpen) {
            const elementToFocus = drawerRef.current.querySelector(initialFocus);
            elementToFocus?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: ref.current needed to detect DOM readiness for focus
    }, [initialFocus, drawerRef.current, drawerState.isOpen]);
    const CloseButton = () => {
        return (React__default.createElement(Button, { className: 'drawer-close-button--rev', variant: 'secondary', size: 'large', "aria-label": 'Close dialog', rounding: 'full', icon: React__default.createElement(CloseRounded, null), onPress: () => drawerState.close() }));
    };
    React__default.Children.toArray(children).forEach((child) => {
        if (!React__default.isValidElement(child))
            return;
        switch (child.type) {
            case DrawerHeader:
                slots.header = child;
                break;
            case DrawerContent:
                slots.content = child;
                break;
            case DrawerFooter:
                slots.footer = child;
        }
    });
    return (React__default.createElement(ModalOverlay, { "data-rev-component": 'drawer', "data-rev-state": drawerState.isOpen ? 'open' : 'closed', "data-rev-placement": placement, className: className || '', isDismissable: isDismissable, isKeyboardDismissDisabled: isKeyboardDismissDisabled, "aria-label": ariaLabel || 'Drawer' },
        React__default.createElement(Modal, { className: 'drawer-modal--rev' },
            !hideOverlay && React__default.createElement("div", { className: 'drawer-modal-overlay--rev' }),
            React__default.createElement(Dialog, { ref: drawerRef, "aria-label": ariaLabel || 'Drawer', className: 'drawer-dialog--rev', "data-rev-size": size, "aria-labelledby": titleId ?? undefined, "aria-describedby": description ? descriptionId : undefined, "data-rev-rounding": rounding },
                React__default.createElement("header", { className: 'drawer-header--rev' },
                    slots.header && (React__default.createElement(Text, { size: 'heading', weight: 'semibold', id: titleId }, slots.header)),
                    hasCloseButton && React__default.createElement(CloseButton, null)),
                React__default.createElement("div", { className: 'drawer-content--rev' },
                    description && (React__default.createElement(Text, { className: 'drawer-description--rev', size: 'bodySmall', id: descriptionId }, description)),
                    React__default.createElement("div", { className: 'drawer-content__main--rev' }, slots.content),
                    slots.footer && (React__default.createElement("footer", { className: 'drawer-footer--rev' }, slots.footer)))))));
};
/**
 * A trigger component that wraps a Button and a Drawer.
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 *     <div>Drawer content</div>
 *   </Drawer>
 * </DrawerTrigger>
 * ```
 *
 *  Or with a render function to access the close function:
 *
 */
const DrawerTrigger = ({ children }) => {
    // Check if the first child is a Button component and
    // enhance it with aria-haspopup if it is
    const enhancedChildren = React__default.Children.map(children, (child, index) => {
        // Only process the first child (the trigger element)
        if (index === 0 && React__default.isValidElement(child)) {
            if (isButtonComponent(child)) {
                // Magically add the aria-haspopup attribute
                return React__default.cloneElement(child, {
                    ...child.props,
                    ariaHaspopup: 'dialog',
                });
            }
        }
        return child;
    });
    return React__default.createElement(DialogTrigger, null, enhancedChildren);
};
/**
 * A component for the content of the Drawer that is meant to be used as a child of the Drawer component. It is used to render the main content of the drawer.
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 * 	   <Drawer.Header>
 *       <div>Drawer header</div>
 *     </Drawer.Header>
 *     <Drawer.Content>
 *       <div>Drawer content</div>
 *     </Drawer.Content>
 *     <Drawer.Footer>
 *       <Button variant='primary'>Save</Button>
 *     </Drawer.Footer>
 *   </Drawer>
 * </DrawerTrigger>
 * ```
 *
 *  Or with a render function to access the close function:
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 *     <Drawer.Header>
 *     {(close) => (
 * 			<Stack>
 * 				<Text>Custom Title</Text>
 * 				<Button onClick={close}>
 * 					Custom Close Button
 *  			</Button>
 * 			</Stack>
 *     )}
 *     </Drawer.Header>
 *     <Drawer.Content>
 *      Add stuff here
 *     </Drawer.Content>
 *  </Drawer>
 * </DrawerTrigger>
 * ```
 * ``` */
const DrawerHeader = ({ children }) => {
    const drawerState = React__default.useContext(OverlayTriggerStateContext);
    return (React__default.createElement("div", { "data-rev-component": 'drawer-header' }, typeof children === 'function' ? children(() => drawerState.close()) : children));
};
/**
 * A component for the content of the Drawer that is meant to be used as a child of the Drawer component. It is used to render the main content of the drawer.
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 *     <Drawer.Content>
 *       <div>Drawer content</div>
 *     </Drawer.Content>
 *     <Drawer.Footer>
 *       <Button variant='primary'>Save</Button>
 *     </Drawer.Footer>
 *   </Drawer>
 * </DrawerTrigger>
 * ```
 *  Or with a render function to access the close function:
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 *     <Drawer.Header>Title</Drawer.Header>
 *     <Drawer.Content>
 *      {(close) => (
 * 			<Stack>
 * 				<Text>Custom Title</Text>
 * 				<Button onClick={close}>
 * 					Custom Close Button
 *  			</Button>
 * 			</Stack>
 *     )}
 *     </Drawer.Content>
 *  </Drawer>
 * </DrawerTrigger>
 * ```
 *
 */
const DrawerContent = ({ children, margin, padding = { x: '24' } }) => {
    const drawerState = React__default.useContext(OverlayTriggerStateContext);
    return (React__default.createElement("div", { "data-rev-component": 'drawer-content', ...toSpacingAttributes('m', margin), ...toSpacingAttributes('p', padding) }, typeof children === 'function' ? children(() => drawerState.close()) : children));
};
/**
 * A footer component for the Drawer that is meant to be used as a child of the Drawer component. It is used to render the footer of the drawer, which typically contains action buttons like "Save" or "Cancel".
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 *     <Drawer.Content>
 *       <div>Drawer content</div>
 *     </Drawer.Content>
 *     <Drawer.Footer>
 *       <Button variant='primary'>Save</Button>
 *     </Drawer.Footer>
 *   </Drawer>
 * </DrawerTrigger>
 * ```
 *
 *  Or with a render function to access the close function:
 *
 * ```tsx
 * <DrawerTrigger>
 *   <Button>Open drawer</Button>
 *   <Drawer>
 *     <Drawer.Header>Title</Drawer.Header>
 * 	   <Drawer.Content>Some Drawer content</Drawer.Content>
 *     <Drawer.Footer>
 *      {(close) => (
 * 			<Stack>
 * 				<Text>Custom Footer Message</Text>
 * 				<Button onClick={close}>
 * 					Custom Close Button
 *  			</Button>
 * 			</Stack>
 *     )}
 *     </Drawer.Footer>
 *  </Drawer>
 * </DrawerTrigger>
 */
const DrawerFooter = ({ children, margin, padding = '24' }) => {
    const drawerState = React__default.useContext(OverlayTriggerStateContext);
    return (React__default.createElement("div", { "data-rev-component": 'drawer-footer', ...toSpacingAttributes('m', margin), ...toSpacingAttributes('p', padding) }, typeof children === 'function' ? children(() => drawerState.close()) : children));
};
Drawer.Header = DrawerHeader;
Drawer.Content = DrawerContent;
Drawer.Footer = DrawerFooter;

export { Drawer, DrawerTrigger };
//# sourceMappingURL=Drawer.js.map
